import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { overlayStack } from '../../lib/overlayStack';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  zIndexClass?: string;
}

export function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  icon: Icon,
  children,
  footer,
  maxWidth = 'md',
  zIndexClass = 'z-[90]',
}: ModalProps) {
  // Register with overlay stack for ESC and body scroll management
  const overlayIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (isOpen) {
      overlayIdRef.current = overlayStack.push(onClose);
    } else if (overlayIdRef.current) {
      overlayStack.remove(overlayIdRef.current);
      overlayIdRef.current = null;
    }
    return () => {
      if (overlayIdRef.current) {
        overlayStack.remove(overlayIdRef.current);
        overlayIdRef.current = null;
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isBottomMost = overlayIdRef.current ? overlayStack.isBottomMost(overlayIdRef.current) : true;

  const maxWidthClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
  }[maxWidth];

  return (
    <div 
      className={`fixed inset-0 ${zIndexClass} flex items-center justify-center p-4 overflow-y-auto`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      {/* Backdrop */}
      <div
        className={`fixed inset-0 transition-opacity duration-300 animate-fade-in ${
          isBottomMost ? 'bg-black/60 backdrop-blur-md' : 'bg-black/30'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal Card */}
      <div
        className={`relative w-full ${maxWidthClasses} bg-surface border border-border shadow-2xl rounded-2xl flex flex-col transform transition-transform duration-300 ease-out animate-scale-in my-auto max-h-[90vh] z-10`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border/60 bg-surface-secondary/40 rounded-t-2xl shrink-0">
          <div className="flex items-center gap-3">
            {Icon && (
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange border border-brand-orange/20">
                <Icon className="h-5 w-5" />
              </div>
            )}
            <div>
              <h3 id="modal-title" className="text-base font-black text-text-primary leading-tight">{title}</h3>
              {subtitle && <p className="text-xs text-text-secondary mt-0.5">{subtitle}</p>}
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-xl p-2 text-text-tertiary hover:bg-surface-tertiary hover:text-text-primary transition-colors cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Cerrar modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border/60 bg-surface-secondary/40 rounded-b-2xl shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
